'use client';

import { useState } from 'react';
import { publicRoutes } from '@/shared/lib/public-routes';
import { DEFAULT_LANGUAGE } from '@/shared/localization';
import { RevealOnScroll } from '@/shared/ui/reveal-on-scroll';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import styles from './styles.module.scss';

type FaqItem = {
  question: string;
  answer: string;
};

const FAQ_ITEM_INDEXES = [0, 1, 2, 3, 4] as const;

function PlusIcon() {
  return (
    <span aria-hidden="true" className={styles.icon}>
      <span className={styles.iconBarVertical} />
      <span className={styles.iconBarHorizontal} />
    </span>
  );
}

function FaqCard({
  item,
  active,
  onToggle,
  index,
}: {
  item: FaqItem;
  active: boolean;
  onToggle: () => void;
  index: number;
}) {
  return (
    <div
      className={`${styles.card} ${active ? styles.cardActive : styles.cardIdle}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={active}
        aria-controls={`faq-answer-${index}`}
        id={`faq-trigger-${index}`}
        className={styles.trigger}
      >
        <span className={styles.question}>{item.question}</span>
        <span
          className={`${styles.toggle} ${active ? styles.toggleActive : ''}`}
        >
          <PlusIcon />
        </span>
      </button>

      <div
        className={`${styles.answerWrap} ${active ? styles.answerWrapOpen : styles.answerWrapClosed}`}
      >
        <div className={styles.answerInner}>
          <p
            id={`faq-answer-${index}`}
            role="region"
            aria-labelledby={`faq-trigger-${index}`}
            className={styles.answer}
          >
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export function SecFaq() {
  const t = useTranslations('intro');
  const params = useParams<{ locale: string }>();
  const locale = params?.locale ?? DEFAULT_LANGUAGE;
  const [activeIndex, setActiveIndex] = useState(0);

  const faqItems: FaqItem[] = FAQ_ITEM_INDEXES.map((index) => ({
    question: t(`faq.items.${index}.question`),
    answer: t(`faq.items.${index}.answer`),
  }));

  const handleToggle = (index: number) => {
    setActiveIndex((current) => (current === index ? -1 : index));
  };

  return (
    <section id="faq" className={styles.section}>
      <div className={styles.outer}>
        <RevealOnScroll delayMs={60} y={16} className={styles.titleWrap}>
          <h2 className={styles.title}>FAQ</h2>
        </RevealOnScroll>

        <RevealOnScroll delayMs={150} y={18} className={styles.panelWrap}>
          <div className={styles.panel}>
            <div className={styles.content}>
              <div className={styles.list}>
                {faqItems.map((item, index) => (
                  <FaqCard
                    key={item.question}
                    item={item}
                    active={index === activeIndex}
                    index={index}
                    onToggle={() => handleToggle(index)}
                  />
                ))}
              </div>

              {/* TODO(landing): consider /api/pages?category=faq integration */}
              <div className={styles.footer}>
                <Link
                  href={publicRoutes.blog({ locale })}
                  className={`${styles.button} ${styles.buttonFaq}`}
                >
                  {t('faq.moreLink')}
                </Link>
                <a
                  href="mailto:vaetki-commerce@ncsoft.com"
                  className={`${styles.button} ${styles.buttonContact}`}
                >
                  {t('faq.contact')}
                </a>
              </div>
            </div>
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
}
